import React from "react";
import { useSelector } from "react-redux";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { BsBoxSeam } from "react-icons/bs";
import { LiaEdit } from "react-icons/lia";
import { AppState } from "../../redux/store";
import { RoleBundle } from "../../types/models/RoleBundle";
import { RoleState } from "../../types/models/RoleState";
import { fontColor } from "../../constants/ColorSets";

const BoxIcon = BsBoxSeam as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = LiaEdit as React.FC<React.SVGProps<SVGSVGElement>>;

interface RoleBundleItemProp {
  roleBundle: RoleBundle;
  onEdit: (roleBundle: RoleBundle) => void;
}

export const RoleBundleItem: React.FC<RoleBundleItemProp> = ({ roleBundle, onEdit }) => {
  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  const getRoleName = (roleId: string) => {
    const role = roleState.knownRoles?.roles.find(
      (knownRole) => knownRole.RoleId.toLowerCase() === roleId.toLowerCase()
    );
    return role?.DisplayName;
  };

  const onClickEditButton = () => {
    onEdit(roleBundle);
  };

  return (
    <Box>
      <Grid container width="100%" wrap="nowrap" alignItems="stretch">
        <Grid
          item
          sx={{
            width: "60px",
            flexShrink: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              mt: 1.5,
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: fontColor.orangeHoverBackground,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <BoxIcon fontSize={20} color={fontColor.orangeTitle} />
          </Box>
        </Grid>
        <Grid
          item
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Typography
              fontSize={18}
              color={fontColor.titleFontColor}
              sx={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                mr: 1,
              }}
            >
              Role Bundle:
            </Typography>
            <Typography
              fontWeight={600}
              fontSize={18}
              color={fontColor.titleFontColor}
              sx={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {roleBundle.DisplayName}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignContent: "flex-start",
              width: "100%",
              gap: 1,
              mt: 1,
            }}
          >
            {roleBundle.RoleIds.map((roleId) => (
              <Box
                key={roleId}
                sx={{
                  borderRadius: 1,
                  backgroundColor: fontColor.grayBackground,
                  py: 0.5,
                  px: 1,
                  fontSize: 14,
                  fontWeight: 500,
                  color: fontColor.titleFontColor,
                }}
              >
                {getRoleName(roleId)}
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid
          item
          sx={{
            width: "120px",
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
          }}
        >
          <Typography fontSize={15} color={fontColor.subTitleFontColor} sx={{ mt: 2.2 }}>
            {roleBundle.RoleIds.length} roles
          </Typography>
          <IconButton sx={{ m: 1 }} onClick={() => onClickEditButton()}>
            <EditIcon color={fontColor.subTitleFontColor} />
          </IconButton>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: "solid lightgrey 1px" }} />
    </Box>
  );
};
